"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { FiInfo } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { BiWorld } from "react-icons/bi";
import { GiBrain } from "react-icons/gi";
import {
  HiOutlineBookOpen,
  HiOutlineMicrophone,
  HiOutlinePencilAlt,
  HiOutlineVolumeUp,
} from "react-icons/hi";
import { MdOutlineChat } from "react-icons/md";
import { IoGameControllerOutline } from "react-icons/io5";
import { TbCategory2 } from "react-icons/tb";
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import ConfirmPopUp from "../../popup/confirmPopUp";
import StatusPopUp from "../../popup/statusPopUp";
import { buildGameSuccessStatus } from "../shared/gameStatusMessages";
import { areCompleteTheSentenceItemsSaveReady } from "../shared/mediaValidation";
import completeTheSentenceAPI from "../../../services/completeTheSentence/completeTheSentence";
import uploadAPI from "../../../services/upload/uploadAPI";
import mainAPI from "../../../services/main/mainAPI";
import { collectPersistedIdsFromApiRows } from "../shared/serverPersistedIds";
import {
  MEDIA_BTN_DELETE_MD,
  MEDIA_BTN_DELETE_SM,
} from "../shared/mediaIconButtonClasses";
import MediaEditGroup from "../shared/MediaEditGroup";
import { GamePreviewModal } from "../shared/GamePreviewModal";
import {
  createCompleteTheSentenceItemsGameSnapshot,
  getCanGamePreview,
  getShouldConfirmLeaveGameEditor,
  patchSavedCompleteTheSentenceItemsSnapshotAfterItemRemoved,
  patchSavedCompleteTheSentenceItemsSnapshotAfterMediaDeleted,
  PREVIEW_BUTTON_DISABLED_TITLE,
  GAME_PREVIEW_BUTTON_CLASS,
  PREVIEW_UNSAVED_MESSAGE,
  PREVIEW_UNSAVED_TITLE,
  useHasUnsavedGameFormChanges,
} from "../shared/gameFormSnapshot";
import { useReportGameEditorLeaveGuard } from "../shared/GameEditorLeaveGuard";
import {
  completeSentenceFieldTarget,
  createGameFieldErrorPopUp,
  type GameStatusPopUpState,
  useGameFieldErrorHighlight,
} from "../shared/fieldErrorHighlight";

type QuestionAnswerChoice = {
  word: string;
  word_sound: string | null;
  answer: string | null;
  answer_sound: string | null;
  answer_image: string | null;
};

type WrongAnswerGroupChoice = {
  wrong_answer: WrongAnswerChoice[];
};

type WrongAnswerChoice = {
  text?: string | null;
  sound?: string | null;
  image?: string | null;
};

type WrongAnswerEditorValue = {
  text: string;
  sound: string | null;
  soundFile: File | null;
  image: string | null;
  imageFile: File | null;
};

type SegmentAnswer = {
  correctAnswer: string;
  correctAnswerSound: string | null;
  correctAnswerSoundFile: File | null;
  correctAnswerImage: string | null;
  correctAnswerImageFile: File | null;
};

type CompleteTheSentenceItem = {
  id: number;
  no: number;
  sentenceText: string;
  segments: SegmentAnswer[];
  wrongChoices: WrongAnswerEditorValue[];
  hasHint: boolean;
  hintText: string;
  imageQuestionFileName: string;
  imageQuestionFile: File | null;
  imageQuestionUrl: string | null;
  audioQuestionFileName: string;
  audioQuestionFile: File | null;
  audioQuestionUrl: string | null;
  imageHintFileName: string;
  imageHintFile: File | null;
  imageHintUrl: string | null;
  audioHintFileName: string;
  audioHintFile: File | null;
  audioHintUrl: string | null;
};

type CompleteTheSentenceQuestionAnswerInput = {
  word?: string | null;
  word_sound?: string | null;
  answer?: string | null;
  answer_sound?: string | null;
  answer_image?: string | null;
  wrong_answer?: Array<
    | string
    | {
        text?: string | null;
        sound?: string | null;
        image?: string | null;
      }
  > | null;
};

type CompleteTheSentenceHardDeleteField =
  | "sound_question"
  | "image_question"
  | "hint"
  | "sound_hint"
  | "image_hint";

type PendingMediaDelete = {
  itemId: number;
  field: CompleteTheSentenceHardDeleteField;
};

type UploadTarget = {
  key: string;
  file: File;
  fileName: string;
};

type GameSkills = {
  skill_listening_speaking: boolean;
  skill_lang_nature: boolean;
  skill_reading: boolean;
  skill_writing: boolean;
};

type GameSkillKey = keyof GameSkills;

const DEFAULT_GAME_SKILLS: GameSkills = {
  skill_listening_speaking: false,
  skill_lang_nature: false,
  skill_reading: false,
  skill_writing: false,
};

type GameAudios = {
  audio_question: boolean;
  audio_choice: boolean;
  audio_assistant_voice: boolean;
  audio_background: boolean;
};

type GameAudiosKey = keyof GameAudios;

type StatusToggleDialogContent = {
  confirmTitle: string;
  confirmMessage: string;
  successTitle: string;
  successMessage: string;
};

const DEFAULT_GAME_AUDIOS: GameAudios = {
  audio_question: false,
  audio_choice: false,
  audio_assistant_voice: false,
  audio_background: false,
};

type CompleteTheSentenceProps = {
  createdGameData?: {
    id: number | string;
    exercise_name?: string;
    uuid?: string;
    description?: string | null;
    status?: boolean | "true" | "false" | number | string | null;
  } | null;
  initialGameDetail?: {
    game_info?: {
      id?: number | string;
      uuid?: string;
      exercise_name?: string;
      suggestion?: string | null;
      description?: string | null;
      status?: boolean | "true" | "false" | number | string | null;
      skill_listening_speaking?: boolean | "true" | "false" | null;
      skill_lang_nature?: boolean | "true" | "false" | null;
      skill_reading?: boolean | "true" | "false" | null;
      skill_writing?: boolean | "true" | "false" | null;
      audio_question?: boolean | "true" | "false" | null;
      audio_choice?: boolean | "true" | "false" | null;
      audio_assistant_voice?: boolean | "true" | "false" | null;
      audio_background?: boolean | "true" | "false" | null;
    };
    questions?: Array<{
      id?: number;
      no?: number;
      question_answer?: CompleteTheSentenceQuestionAnswerInput[];
      hint?: string | null;
      image_question_answer?: string | null;
      sound_question_answer?: string | null;
      image_hint?: string | null;
      sound_hint?: string | null;
      skill_listening_speaking?: boolean | "true" | "false" | null;
      skill_lang_nature?: boolean | "true" | "false" | null;
      skill_reading?: boolean | "true" | "false" | null;
      skill_writing?: boolean | "true" | "false" | null;
      audio_question?: boolean | "true" | "false" | null;
      audio_choice?: boolean | "true" | "false" | null;
      audio_assistant_voice?: boolean | "true" | "false" | null;
      audio_background?: boolean | "true" | "false" | null;
    }>;
  } | null;
  onCompleted?: () => void;
};

const MIN_ITEMS = 1;
const MAX_ITEMS = 10;

const SOUND_UPLOAD_KEY_SEPARATOR = "::";

function createCorrectAnswerSoundUploadKey(itemId: number, blankIndex: number) {
  return `cts-correct-sound${SOUND_UPLOAD_KEY_SEPARATOR}${itemId}${SOUND_UPLOAD_KEY_SEPARATOR}${blankIndex}`;
}

function createWrongAnswerSoundUploadKey(itemId: number, wrongIndex: number) {
  return `cts-wrong-sound${SOUND_UPLOAD_KEY_SEPARATOR}${itemId}${SOUND_UPLOAD_KEY_SEPARATOR}${wrongIndex}`;
}

function createCorrectAnswerImageUploadKey(itemId: number, blankIndex: number) {
  return `cts-correct-image${SOUND_UPLOAD_KEY_SEPARATOR}${itemId}${SOUND_UPLOAD_KEY_SEPARATOR}${blankIndex}`;
}

function createWrongAnswerImageUploadKey(itemId: number, wrongIndex: number) {
  return `cts-wrong-image${SOUND_UPLOAD_KEY_SEPARATOR}${itemId}${SOUND_UPLOAD_KEY_SEPARATOR}${wrongIndex}`;
}

function getIncomingCompleteTheSentenceRows(
  detail: CompleteTheSentenceProps["initialGameDetail"],
): unknown[] {
  if (!detail) {
    return [];
  }

  const detailRecord = detail as Record<string, unknown>;
  if (Array.isArray(detailRecord.questions)) {
    return detailRecord.questions as unknown[];
  }
  if (Array.isArray(detailRecord.complete_the_sentence_rows)) {
    return detailRecord.complete_the_sentence_rows as unknown[];
  }

  return [];
}

function hasPersistedCompleteTheSentenceRows(
  detail: CompleteTheSentenceProps["initialGameDetail"],
): boolean {
  return getIncomingCompleteTheSentenceRows(detail).length > 0;
}

function getFileExtension(fileName: string) {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === fileName.length - 1) {
    return "";
  }
  return fileName.slice(dotIndex);
}

function getFileNameFromPath(path: string | null | undefined) {
  if (!path) {
    return "";
  }

  const cleanPath = String(path);
  const bySlash = cleanPath.split("/");
  const candidate = bySlash[bySlash.length - 1] || "";
  return decodeURIComponent(candidate);
}

function buildUploadedMediaUrl(path: string | null | undefined) {
  if (!path) {
    return null;
  }

  const raw = String(path).trim();
  if (!raw) {
    return null;
  }

  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    return raw;
  }

  const baseUrl =
    process.env.NEXT_PUBLIC_BACKEND_URL ||
    "https://thaipilacreate.gforcesolution.com";
  const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
  const normalizedPath = raw.replace(/^\/+/, "");
  if (!normalizedPath) {
    return null;
  }

  if (normalizedPath.startsWith("api/")) {
    return `${normalizedBaseUrl}/${normalizedPath}`;
  }

  return `${normalizedBaseUrl}/api/${normalizedPath}`;
}

function normalizeSkillBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }

  return null;
}

function normalizeStatus(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "1";
}

function normalizeExpectedTime(value: unknown): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return "0";
  }
  return String(Math.floor(parsed));
}

function getPublishButtonClass(published: boolean): string {
  const base =
    "cursor-pointer inline-flex h-9 w-[116px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-75";

  if (published) {
    return `${base} bg-[#E6F8EF]`;
  }

  return `${base} bg-[#E0E0E0]`;
}

function renderPublishStatusContent(
  published: boolean,
  labels: { publish: string; unpublish: string },
  iconSize = 16,
) {
  if (published) {
    return (
      <>
        <span className="inline-flex shrink-0 items-center justify-center">
          <BiWorld size={iconSize} color="#16A34A" />
        </span>
        <span className="text-[#16A34A]">{labels.publish}</span>
      </>
    );
  }

  return (
    <>
      <span className="inline-flex shrink-0 items-center justify-center">
        <BiWorld size={iconSize} color="#3b3b3b" />
      </span>
      <span className="text-[#3b3b3b]">{labels.unpublish}</span>
    </>
  );
}

function getStatusToggleDialogContent(
  currentStatus: boolean,
  labels: {
    confirmPublish: string;
    publishPrompt: string;
    published: string;
    publishedMessage: string;
    confirmUnpublish: string;
    unpublishPrompt: string;
    unpublished: string;
    unpublishedMessage: string;
  },
): StatusToggleDialogContent {
  const nextStatus = !currentStatus;

  if (nextStatus) {
    return {
      confirmTitle: labels.confirmPublish,
      confirmMessage: labels.publishPrompt,
      successTitle: labels.published,
      successMessage: labels.publishedMessage,
    };
  }

  return {
    confirmTitle: labels.confirmUnpublish,
    confirmMessage: labels.unpublishPrompt,
    successTitle: labels.unpublished,
    successMessage: labels.unpublishedMessage,
  };
}

function normalizeGameSkillsOrNull(
  value: Partial<Record<GameSkillKey, unknown>>,
) {
  const listening = normalizeSkillBoolean(value.skill_listening_speaking);
  const langNature = normalizeSkillBoolean(value.skill_lang_nature);
  const reading = normalizeSkillBoolean(value.skill_reading);
  const writing = normalizeSkillBoolean(value.skill_writing);

  if (
    listening === null ||
    langNature === null ||
    reading === null ||
    writing === null
  ) {
    return null;
  }

  return {
    skill_listening_speaking: listening,
    skill_lang_nature: langNature,
    skill_reading: reading,
    skill_writing: writing,
  };
}

function extractGameSkillsFromUnknown(
  value: Partial<Record<GameSkillKey, unknown>>,
): GameSkills {
  const normalized = normalizeGameSkillsOrNull(value);
  return normalized ?? { ...DEFAULT_GAME_SKILLS };
}

function normalizeGameAudiosOrNull(
  value: Partial<Record<GameAudiosKey, unknown>>,
) {
  const audioQuestion = normalizeSkillBoolean(value.audio_question);
  const audioChoice = normalizeSkillBoolean(value.audio_choice);
  const audioAssistantVoice = normalizeSkillBoolean(
    value.audio_assistant_voice,
  );
  const audioBackground = normalizeSkillBoolean(value.audio_background);

  if (
    audioQuestion === null ||
    audioChoice === null ||
    audioAssistantVoice === null ||
    audioBackground === null
  ) {
    return null;
  }

  return {
    audio_question: audioQuestion,
    audio_choice: audioChoice,
    audio_assistant_voice: audioAssistantVoice,
    audio_background: audioBackground,
  };
}

function areGameAudiosEqual(left: GameAudios, right: GameAudios) {
  return (
    left.audio_question === right.audio_question &&
    left.audio_choice === right.audio_choice &&
    left.audio_assistant_voice === right.audio_assistant_voice &&
    left.audio_background === right.audio_background
  );
}

function extractGameAudiosFromUnknown(
  value: Partial<Record<GameAudiosKey, unknown>>,
): GameAudios {
  const normalized = normalizeGameAudiosOrNull(value);
  return normalized ?? { ...DEFAULT_GAME_AUDIOS };
}

function createEmptyCompleteTheSentenceItem(
  id: number,
  no: number,
): CompleteTheSentenceItem {
  return {
    id,
    no,
    sentenceText: "",
    segments: [],
    wrongChoices: [],
    hasHint: false,
    hintText: "",
    imageQuestionFileName: "",
    imageQuestionFile: null,
    imageQuestionUrl: null,
    audioQuestionFileName: "",
    audioQuestionFile: null,
    audioQuestionUrl: null,
    imageHintFileName: "",
    imageHintFile: null,
    imageHintUrl: null,
    audioHintFileName: "",
    audioHintFile: null,
    audioHintUrl: null,
  };
}

function countBlanksFromSentence(sentenceText: string) {
  return (sentenceText.match(/_/g) || []).length;
}

function removeBlankAtIndex(sentenceText: string, blankIndex: number) {
  let underscoreCount = 0;
  let result = "";

  for (const char of sentenceText) {
    if (char === "_") {
      if (underscoreCount === blankIndex) {
        underscoreCount += 1;
        continue;
      }
      underscoreCount += 1;
    }
    result += char;
  }

  return result;
}

function normalizeSegmentArray(
  segments: SegmentAnswer[],
  targetLength: number,
) {
  const next = [...segments];

  if (next.length > targetLength) {
    return next.slice(0, targetLength);
  }

  while (next.length < targetLength) {
    next.push({
      correctAnswer: "",
      correctAnswerSound: null,
      correctAnswerSoundFile: null,
      correctAnswerImage: null,
      correctAnswerImageFile: null,
    });
  }

  return next;
}

function normalizeWrongAnswerEntry(
  entry: unknown,
): WrongAnswerEditorValue | null {
  if (typeof entry === "string") {
    const textValue = entry.trim();
    if (!textValue) {
      return null;
    }
    return {
      text: textValue,
      sound: null,
      soundFile: null,
      image: null,
      imageFile: null,
    };
  }

  if (!entry || typeof entry !== "object") {
    return null;
  }

  const record = entry as { text?: unknown; sound?: unknown; image?: unknown };
  const textValue = record.text == null ? "" : String(record.text).trim();
  const soundValue = buildUploadedMediaUrl(
    record.sound == null ? null : String(record.sound).trim() || null,
  );
  const imageValue = buildUploadedMediaUrl(
    record.image == null ? null : String(record.image).trim() || null,
  );

  if (!textValue && !soundValue && !imageValue) {
    return null;
  }

  return {
    text: textValue,
    sound: soundValue,
    soundFile: null,
    image: imageValue,
    imageFile: null,
  };
}

function parseQuestionAnswerToEditorState(raw: unknown) {
  const questionAnswer = Array.isArray(raw)
    ? (raw as Array<Record<string, unknown>>)
    : [];

  let sentenceText = "";
  const segments: SegmentAnswer[] = [];
  const wrongChoices: WrongAnswerEditorValue[] = [];
  const legacyWrongChoices: WrongAnswerEditorValue[] = [];

  questionAnswer.forEach((part) => {
    const hasWrongGroupOnly =
      Array.isArray(part.wrong_answer) &&
      !("word" in part) &&
      !("answer" in part);
    if (hasWrongGroupOnly) {
      const wrongAnswerList = Array.isArray(part.wrong_answer)
        ? part.wrong_answer
        : [];
      const parsedWrongChoices = wrongAnswerList
        .map((value) => normalizeWrongAnswerEntry(value))
        .filter((value): value is WrongAnswerEditorValue => value !== null);
      wrongChoices.push(...parsedWrongChoices);
      return;
    }

    const word = String(part.word ?? "");
    const answer = part.answer == null ? "" : String(part.answer);
    const answerSound = buildUploadedMediaUrl(
      part.answer_sound == null
        ? null
        : String(part.answer_sound).trim() || null,
    );
    const answerImage = buildUploadedMediaUrl(
      part.answer_image == null
        ? null
        : String(part.answer_image).trim() || null,
    );

    if (word.toLowerCase() === "null") {
      const wrongAnswerArray = Array.isArray(part.wrong_answer)
        ? part.wrong_answer
            .map((value) => normalizeWrongAnswerEntry(value))
            .filter((value): value is WrongAnswerEditorValue => value !== null)
        : [];

      sentenceText += "_";
      segments.push({
        correctAnswer: answer,
        correctAnswerSound: answerSound,
        correctAnswerSoundFile: null,
        correctAnswerImage: answerImage,
        correctAnswerImageFile: null,
      });
      legacyWrongChoices.push(...wrongAnswerArray);
      return;
    }

    if (word && word.toLowerCase() !== "null") {
      sentenceText += word;
    }
  });

  return {
    sentenceText,
    segments,
    wrongChoices: wrongChoices.length > 0 ? wrongChoices : legacyWrongChoices,
  };
}

function buildQuestionAnswerPayload(
  item: CompleteTheSentenceItem,
): Array<QuestionAnswerChoice | WrongAnswerGroupChoice> {
  const segments = item.sentenceText.split("_");
  const payload: Array<QuestionAnswerChoice | WrongAnswerGroupChoice> = [];

  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    if (segment) {
      payload.push({
        word: segment,
        word_sound: null,
        answer: null,
        answer_sound: null,
        answer_image: null,
      });
    }

    if (index < segments.length - 1) {
      const segmentAnswer = item.segments[index];

      payload.push({
        word: "null",
        answer: segmentAnswer?.correctAnswer?.trim() || null,
        word_sound: null,
        answer_sound: segmentAnswer?.correctAnswerSound
          ? String(segmentAnswer.correctAnswerSound).trim() || null
          : null,
        answer_image: segmentAnswer?.correctAnswerImage
          ? String(segmentAnswer.correctAnswerImage).trim() || null
          : null,
      });
    }
  }

  const wrongAnswerGroup = item.wrongChoices
    .map((value) => {
      const textValue = String(value.text ?? "").trim();
      const soundValue = value.sound
        ? String(value.sound).trim() || null
        : null;
      const imageValue = value.image
        ? String(value.image).trim() || null
        : null;
      if (!textValue && !soundValue && !imageValue) {
        return null;
      }
      return {
        text: textValue || null,
        sound: soundValue,
        image: imageValue,
      };
    })
    .filter(
      (
        value,
      ): value is {
        text: string | null;
        sound: string | null;
        image: string | null;
      } => value !== null,
    );

  if (wrongAnswerGroup.length > 0) {
    payload.push({
      wrong_answer: wrongAnswerGroup,
    });
  }

  return payload;
}

function toSnapshotItems(sourceItems: CompleteTheSentenceItem[]) {
  return sourceItems.map((item) => ({
    ...item,
    segments: item.segments.map((segment) => ({
      correctAnswer: segment.correctAnswer,
    })),
    wrongChoices: item.wrongChoices
      .map((wrong) => wrong.text)
      .filter((text) => text.trim().length > 0),
  }));
}

export default function CompleteTheSentence({
  createdGameData,
  initialGameDetail,
  onCompleted,
}: CompleteTheSentenceProps) {
  const tCompleteTheSentence = useTranslations("completeTheSentence");
  const tMain = useTranslations("main");
  const nextItemIdRef = useRef(6);
  const initialExerciseNameRef = useRef(createdGameData?.exercise_name ?? "");
  const initialSuggestionRef = useRef("");
  const initialDescriptionRef = useRef("");
  const initialItemIdsRef = useRef<Set<number>>(new Set());
  const initialGameSkillsRef = useRef<GameSkills>({ ...DEFAULT_GAME_SKILLS });
  const initialGameAudiosRef = useRef<GameAudios>({ ...DEFAULT_GAME_AUDIOS });
  const savedFormSnapshotRef = useRef<ReturnType<
    typeof createCompleteTheSentenceItemsGameSnapshot
  > | null>(null);

  const [exerciseName, setExerciseName] = useState(
    createdGameData?.exercise_name ?? "",
  );
  const [expectedTime, setExpectedTime] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
  const [pendingDeleteItemId, setPendingDeleteItemId] = useState<number | null>(
    null,
  );
  const [isUpdateMode, setIsUpdateMode] = useState(
    hasPersistedCompleteTheSentenceRows(initialGameDetail),
  );
  const [isPublished, setIsPublished] = useState(
    normalizeStatus(
      initialGameDetail?.game_info?.status ?? createdGameData?.status ?? false,
    ),
  );
  const [isStatusConfirmOpen, setIsStatusConfirmOpen] = useState(false);
  const [isStatusConfirming, setIsStatusConfirming] = useState(false);
  const [statusPopUp, setStatusPopUp] = useState<GameStatusPopUpState | null>(
    null,
  );
  const {
    fieldErrorHighlightKey,
    getFieldBorderClass,
    clearFieldErrorHighlight,
    openStatusPopUp,
    closeStatusPopUp,
  } = useGameFieldErrorHighlight();
  const [pendingMediaDelete, setPendingMediaDelete] =
    useState<PendingMediaDelete | null>(null);
  const [isDeletingMedia, setIsDeletingMedia] = useState(false);
  const [previewIframeUrl, setPreviewIframeUrl] = useState<string | null>(null);
  const [gameSkills, setGameSkills] = useState<GameSkills>({
    ...DEFAULT_GAME_SKILLS,
  });
  const [gameAudios, setGameAudios] = useState<GameAudios>({
    ...DEFAULT_GAME_AUDIOS,
  });
  const [items, setItems] = useState<CompleteTheSentenceItem[]>([
    createEmptyCompleteTheSentenceItem(1, 1),
  ]);
  const canAddItem = items.length < MAX_ITEMS;
  const canRemoveItem = items.length > MIN_ITEMS;

  const previewUuid = String(
    initialGameDetail?.game_info?.uuid ?? createdGameData?.uuid ?? "",
  ).trim();
  useEffect(() => {
    setExerciseName(createdGameData?.exercise_name ?? "");
    setIsPublished(normalizeStatus(createdGameData?.status ?? false));
    initialExerciseNameRef.current = createdGameData?.exercise_name ?? "";
  }, [createdGameData?.exercise_name, createdGameData?.status]);

  useEffect(() => {
    if (initialGameDetail?.game_info?.id) {
      return;
    }

    const incomingDescription = String(createdGameData?.description ?? "");
    setDescription(incomingDescription);
    initialDescriptionRef.current = incomingDescription;
  }, [createdGameData?.description, initialGameDetail?.game_info?.id]);

  const applyLoadedGameDetail = (
    detail: CompleteTheSentenceProps["initialGameDetail"],
  ) => {
    if (!detail?.game_info?.id) {
      const createdDescription = String(createdGameData?.description ?? "");
      setSuggestion("");
      setDescription(createdDescription);
      setExpectedTime("");
      initialSuggestionRef.current = "";
      initialDescriptionRef.current = createdDescription;
      initialItemIdsRef.current = new Set();
      initialGameSkillsRef.current = { ...DEFAULT_GAME_SKILLS };
      initialGameAudiosRef.current = { ...DEFAULT_GAME_AUDIOS };
      setGameSkills({ ...DEFAULT_GAME_SKILLS });
      setGameAudios({ ...DEFAULT_GAME_AUDIOS });
      setIsUpdateMode(false);
      savedFormSnapshotRef.current = createCompleteTheSentenceItemsGameSnapshot(
        createdGameData?.exercise_name ?? "",
        "",
        createdDescription,
        { ...DEFAULT_GAME_SKILLS },
        { ...DEFAULT_GAME_AUDIOS },
        toSnapshotItems([
          createEmptyCompleteTheSentenceItem(1, 1),
          // createEmptyCompleteTheSentenceItem(2, 2),
        ]),
      );
      return;
    }

    const gameInfo = detail.game_info;
    const incomingRows = getIncomingCompleteTheSentenceRows(detail);

    const incomingExerciseName = String(gameInfo.exercise_name ?? "");
    const incomingSuggestion = String(gameInfo.suggestion ?? "");
    const incomingDescription = String(gameInfo.description ?? "");
    const incomingStatus = normalizeStatus(gameInfo.status);

    setExerciseName(incomingExerciseName);
    setSuggestion(incomingSuggestion);
    setDescription(incomingDescription);
    setExpectedTime(
      normalizeExpectedTime(
        (incomingRows[0] as Record<string, unknown> | undefined)?.expected_time,
      ),
    );
    setIsPublished(incomingStatus);
    initialExerciseNameRef.current = incomingExerciseName;
    initialSuggestionRef.current = incomingSuggestion;
    initialDescriptionRef.current = incomingDescription;

    const mappedItems =
      incomingRows.length > 0
        ? incomingRows.map((row, index) => {
            const record = row as Record<string, unknown>;
            const mappedId = Number(record.id ?? index + 1);
            const normalizedId =
              Number.isInteger(mappedId) && mappedId > 0 ? mappedId : index + 1;
            const parsed = parseQuestionAnswerToEditorState(
              record.question_answer,
            );
            const hintRaw = String(record.hint ?? "");
            const imageHintRaw = String(record.image_hint ?? "");
            const soundHintRaw = String(record.sound_hint ?? "");
            const imageQuestionRaw = String(record.image_question_answer ?? "");
            const soundQuestionRaw = String(record.sound_question_answer ?? "");

            return {
              id: normalizedId,
              no: Number(record.no ?? index + 1),
              sentenceText: parsed.sentenceText,
              segments: normalizeSegmentArray(
                parsed.segments,
                countBlanksFromSentence(parsed.sentenceText),
              ),
              wrongChoices: parsed.wrongChoices,
              hasHint: Boolean(hintRaw.trim() || imageHintRaw || soundHintRaw),
              hintText: hintRaw,
              imageQuestionFileName: getFileNameFromPath(imageQuestionRaw),
              imageQuestionFile: null,
              imageQuestionUrl: buildUploadedMediaUrl(imageQuestionRaw),
              audioQuestionFileName: getFileNameFromPath(soundQuestionRaw),
              audioQuestionFile: null,
              audioQuestionUrl: buildUploadedMediaUrl(soundQuestionRaw),
              imageHintFileName: getFileNameFromPath(imageHintRaw),
              imageHintFile: null,
              imageHintUrl: buildUploadedMediaUrl(imageHintRaw),
              audioHintFileName: getFileNameFromPath(soundHintRaw),
              audioHintFile: null,
              audioHintUrl: buildUploadedMediaUrl(soundHintRaw),
            };
          })
        : [
            createEmptyCompleteTheSentenceItem(1, 1),
            // createEmptyCompleteTheSentenceItem(2, 2),
          ];

    const skillsFromGameInfo = extractGameSkillsFromUnknown({
      skill_listening_speaking: gameInfo.skill_listening_speaking,
      skill_lang_nature: gameInfo.skill_lang_nature,
      skill_reading: gameInfo.skill_reading,
      skill_writing: gameInfo.skill_writing,
    });

    const firstRow = incomingRows[0] as Record<string, unknown> | undefined;
    const skillsFromRows = extractGameSkillsFromUnknown({
      skill_listening_speaking: firstRow?.skill_listening_speaking,
      skill_lang_nature: firstRow?.skill_lang_nature,
      skill_reading: firstRow?.skill_reading,
      skill_writing: firstRow?.skill_writing,
    });

    const resolvedSkills =
      incomingRows.length > 0 ? skillsFromRows : skillsFromGameInfo;
    setGameSkills(resolvedSkills);
    initialGameSkillsRef.current = resolvedSkills;

    const audiosFromGameInfo = extractGameAudiosFromUnknown({
      audio_question: gameInfo.audio_question,
      audio_choice: gameInfo.audio_choice,
      audio_assistant_voice: gameInfo.audio_assistant_voice,
      audio_background: gameInfo.audio_background,
    });

    const firstRowForAudio = incomingRows[0] as
      | Record<string, unknown>
      | undefined;
    const audiosFromRows = extractGameAudiosFromUnknown({
      audio_question: firstRowForAudio?.audio_question,
      audio_choice: firstRowForAudio?.audio_choice,
      audio_assistant_voice: firstRowForAudio?.audio_assistant_voice,
      audio_background: firstRowForAudio?.audio_background,
    });

    let resolvedAudios =
      incomingRows.length > 0 ? audiosFromRows : audiosFromGameInfo;
    if (incomingRows.length > 1) {
      for (let index = 1; index < incomingRows.length; index += 1) {
        const row = incomingRows[index] as Record<string, unknown>;
        const rowAudios = normalizeGameAudiosOrNull({
          audio_question: row.audio_question,
          audio_choice: row.audio_choice,
          audio_assistant_voice: row.audio_assistant_voice,
          audio_background: row.audio_background,
        });

        if (!rowAudios) {
          console.warn(
            `[CompleteTheSentence] Invalid audio values in row ${index + 1}, using first row values.`,
          );
          continue;
        }

        if (!areGameAudiosEqual(rowAudios, resolvedAudios)) {
          console.warn(
            "[CompleteTheSentence] Audio values are inconsistent across rows. Using the first row values.",
          );
          break;
        }
      }
    }

    setGameAudios(resolvedAudios);
    initialGameAudiosRef.current = resolvedAudios;

    const normalizedItems = mappedItems.map((item, index) => ({
      ...item,
      no: index + 1,
    }));
    setItems(normalizedItems);

    initialItemIdsRef.current = collectPersistedIdsFromApiRows(
      incomingRows,
      (row) => {
        const mappedId = Number((row as Record<string, unknown>).id);
        return Number.isInteger(mappedId) && mappedId > 0 ? mappedId : null;
      },
    );
    nextItemIdRef.current =
      normalizedItems.reduce((max, item) => Math.max(max, item.id), 0) + 1;
    setIsUpdateMode(incomingRows.length > 0);
    savedFormSnapshotRef.current = createCompleteTheSentenceItemsGameSnapshot(
      incomingExerciseName,
      incomingSuggestion,
      incomingDescription,
      resolvedSkills,
      resolvedAudios,
      toSnapshotItems(normalizedItems),
    );
  };

  useEffect(() => {
    applyLoadedGameDetail(initialGameDetail);
  }, [initialGameDetail]);

  const setNoByOrder = (sourceItems: CompleteTheSentenceItem[]) =>
    sourceItems.map((item, index) => ({ ...item, no: index + 1 }));

  const updateGameSkill = (skillKey: GameSkillKey, checked: boolean) => {
    setGameSkills((previous) => ({
      ...previous,
      [skillKey]: checked,
    }));
  };

  const updateGameAudio = (audioKey: GameAudiosKey, checked: boolean) => {
    setGameAudios((previous) => ({
      ...previous,
      [audioKey]: checked,
    }));
  };

  const addItem = () => {
    if (!canAddItem) {
      return;
    }
    const newId = nextItemIdRef.current;
    nextItemIdRef.current += 1;
    setItems((previous) =>
      setNoByOrder([
        ...previous,
        createEmptyCompleteTheSentenceItem(newId, previous.length + 1),
      ]),
    );
  };

  const removeItem = async (id: number) => {
    if (!canRemoveItem) {
      return;
    }

    if (isUpdateMode && initialItemIdsRef.current.has(id)) {
      const deleteResponse = await completeTheSentenceAPI.hardDelete({ id });
      if (deleteResponse?.status === "failed") {
        setStatusPopUp({
          title: tCompleteTheSentence("error"),
          message: tCompleteTheSentence("deleteError"),
          type: "error",
        });
        return;
      }
      initialItemIdsRef.current.delete(id);
      patchSavedCompleteTheSentenceItemsSnapshotAfterItemRemoved(
        savedFormSnapshotRef,
        id,
      );
    }

    setItems((previous) =>
      setNoByOrder(previous.filter((item) => item.id !== id)),
    );
  };

  const moveItem = (id: number, direction: -1 | 1) => {
    setItems((previous) => {
      const currentIndex = previous.findIndex((item) => item.id === id);
      if (currentIndex < 0) {
        return previous;
      }

      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= previous.length) {
        return previous;
      }

      const reordered = [...previous];
      const [moved] = reordered.splice(currentIndex, 1);
      reordered.splice(nextIndex, 0, moved);
      return setNoByOrder(reordered);
    });
  };

  const updateItem = (
    id: number,
    updater: (item: CompleteTheSentenceItem) => CompleteTheSentenceItem,
  ) => {
    setItems((previous) =>
      previous.map((item) => (item.id === id ? updater(item) : item)),
    );
  };

  const updateSentenceText = (id: number, value: string) => {
    if (
      fieldErrorHighlightKey ===
      completeSentenceFieldTarget(id, "sentence").highlightKey
    ) {
      clearFieldErrorHighlight();
    }
    updateItem(id, (item) => {
      const targetBlankCount = countBlanksFromSentence(value);
      return {
        ...item,
        sentenceText: value,
        segments: normalizeSegmentArray(item.segments, targetBlankCount),
      };
    });
  };

  const addBlankToSentence = (id: number) => {
    updateItem(id, (item) => {
      const nextSentenceText = `${item.sentenceText}_`;
      const targetBlankCount = countBlanksFromSentence(nextSentenceText);
      return {
        ...item,
        sentenceText: nextSentenceText,
        segments: normalizeSegmentArray(item.segments, targetBlankCount),
      };
    });
  };

  const removeBlankFromSentence = (id: number, blankIndex: number) => {
    updateItem(id, (item) => {
      const nextSentenceText = removeBlankAtIndex(
        item.sentenceText,
        blankIndex,
      );
      const nextSegments = item.segments.filter(
        (_, index) => index !== blankIndex,
      );
      const targetBlankCount = countBlanksFromSentence(nextSentenceText);
      return {
        ...item,
        sentenceText: nextSentenceText,
        segments: normalizeSegmentArray(nextSegments, targetBlankCount),
      };
    });
  };

  const updateBlankCorrectAnswer = (
    itemId: number,
    blankIndex: number,
    value: string,
  ) => {
    if (
      fieldErrorHighlightKey ===
      completeSentenceFieldTarget(itemId, "blank-correct", blankIndex)
        .highlightKey
    ) {
      clearFieldErrorHighlight();
    }
    updateItem(itemId, (item) => ({
      ...item,
      segments: item.segments.map((segment, index) =>
        index === blankIndex ? { ...segment, correctAnswer: value } : segment,
      ),
    }));
  };

  const updateWrongChoiceText = (
    itemId: number,
    wrongIndex: number,
    value: string,
  ) => {
    if (
      fieldErrorHighlightKey ===
      completeSentenceFieldTarget(itemId, "blank-wrong").highlightKey
    ) {
      clearFieldErrorHighlight();
    }
    updateItem(itemId, (item) => ({
      ...item,
      wrongChoices: item.wrongChoices.map((wrong, targetIndex) =>
        targetIndex === wrongIndex ? { ...wrong, text: value } : wrong,
      ),
    }));
  };

  const addWrongChoice = (itemId: number) => {
    updateItem(itemId, (item) => {
      if (item.wrongChoices.length >= 99) {
        return item;
      }
      return {
        ...item,
        wrongChoices: [
          ...item.wrongChoices,
          {
            text: "",
            sound: null,
            soundFile: null,
            image: null,
            imageFile: null,
          },
        ],
      };
    });
  };

  const updateCorrectAnswerSound = (
    itemId: number,
    blankIndex: number,
    file: File,
  ) => {
    updateItem(itemId, (item) => ({
      ...item,
      segments: item.segments.map((segment, index) =>
        index === blankIndex
          ? {
              ...segment,
              correctAnswerSound: null,
              correctAnswerSoundFile: file,
            }
          : segment,
      ),
    }));
  };

  const removeCorrectAnswerSound = (itemId: number, blankIndex: number) => {
    updateItem(itemId, (item) => ({
      ...item,
      segments: item.segments.map((segment, index) =>
        index === blankIndex
          ? {
              ...segment,
              correctAnswerSound: null,
              correctAnswerSoundFile: null,
            }
          : segment,
      ),
    }));
  };

  const updateCorrectAnswerImage = (
    itemId: number,
    blankIndex: number,
    file: File,
  ) => {
    updateItem(itemId, (item) => ({
      ...item,
      segments: item.segments.map((segment, index) =>
        index === blankIndex
          ? {
              ...segment,
              correctAnswerImage: null,
              correctAnswerImageFile: file,
            }
          : segment,
      ),
    }));
  };

  const removeCorrectAnswerImage = (itemId: number, blankIndex: number) => {
    updateItem(itemId, (item) => ({
      ...item,
      segments: item.segments.map((segment, index) =>
        index === blankIndex
          ? {
              ...segment,
              correctAnswerImage: null,
              correctAnswerImageFile: null,
            }
          : segment,
      ),
    }));
  };

  const updateWrongAnswerSound = (
    itemId: number,
    wrongIndex: number,
    file: File,
  ) => {
    updateItem(itemId, (item) => ({
      ...item,
      wrongChoices: item.wrongChoices.map((wrong, targetIndex) =>
        targetIndex === wrongIndex
          ? { ...wrong, sound: null, soundFile: file }
          : wrong,
      ),
    }));
  };

  const removeWrongAnswerSound = (itemId: number, wrongIndex: number) => {
    updateItem(itemId, (item) => ({
      ...item,
      wrongChoices: item.wrongChoices.map((wrong, targetIndex) =>
        targetIndex === wrongIndex
          ? { ...wrong, sound: null, soundFile: null }
          : wrong,
      ),
    }));
  };

  const updateWrongAnswerImage = (
    itemId: number,
    wrongIndex: number,
    file: File,
  ) => {
    updateItem(itemId, (item) => ({
      ...item,
      wrongChoices: item.wrongChoices.map((wrong, targetIndex) =>
        targetIndex === wrongIndex
          ? { ...wrong, image: null, imageFile: file }
          : wrong,
      ),
    }));
  };

  const removeWrongAnswerImage = (itemId: number, wrongIndex: number) => {
    updateItem(itemId, (item) => ({
      ...item,
      wrongChoices: item.wrongChoices.map((wrong, targetIndex) =>
        targetIndex === wrongIndex
          ? { ...wrong, image: null, imageFile: null }
          : wrong,
      ),
    }));
  };

  const removeWrongChoice = (itemId: number, wrongIndex: number) => {
    updateItem(itemId, (item) => ({
      ...item,
      wrongChoices: item.wrongChoices.filter(
        (_, indexWrong) => indexWrong !== wrongIndex,
      ),
    }));
  };

  const updateHintText = (id: number, value: string) => {
    updateItem(id, (item) => ({
      ...item,
      hasHint: true,
      hintText: value,
    }));
  };

  const setFileOnItem = (
    id: number,
    options: {
      file: File;
      fileNameField:
        | "imageQuestionFileName"
        | "audioQuestionFileName"
        | "imageHintFileName"
        | "audioHintFileName";
      fileField:
        | "imageQuestionFile"
        | "audioQuestionFile"
        | "imageHintFile"
        | "audioHintFile";
      urlField:
        | "imageQuestionUrl"
        | "audioQuestionUrl"
        | "imageHintUrl"
        | "audioHintUrl";
      forceEnableHint?: boolean;
    },
  ) => {
    updateItem(id, (item) => ({
      ...item,
      [options.fileNameField]: options.file.name,
      [options.fileField]: options.file,
      [options.urlField]: null,
      ...(options.forceEnableHint ? { hasHint: true } : {}),
    }));
  };

  const clearHint = (id: number) => {
    updateItem(id, (item) => ({
      ...item,
      hasHint: false,
      hintText: "",
      imageHintFileName: "",
      imageHintFile: null,
      imageHintUrl: null,
      audioHintFileName: "",
      audioHintFile: null,
      audioHintUrl: null,
    }));
  };

  const getDeleteFieldLabel = (field: CompleteTheSentenceHardDeleteField) => {
    switch (field) {
      case "image_question":
        return tCompleteTheSentence("questionImageLabel");
      case "sound_question":
        return tCompleteTheSentence("questionAudioLabel");
      case "image_hint":
        return tCompleteTheSentence("hintImageLabel");
      case "sound_hint":
        return tCompleteTheSentence("hintAudioLabel");
      case "hint":
        return tCompleteTheSentence("hint");
      default:
        return tCompleteTheSentence("dataLabel");
    }
  };

  const requestDeleteMediaField = (
    itemId: number,
    field: CompleteTheSentenceHardDeleteField,
  ) => {
    setPendingMediaDelete({ itemId, field });
  };

  const clearMediaFieldFromItem = (
    itemId: number,
    field: CompleteTheSentenceHardDeleteField,
  ) => {
    if (field === "hint") {
      clearHint(itemId);
      return;
    }

    updateItem(itemId, (item) => {
      if (field === "image_question") {
        return {
          ...item,
          imageQuestionFileName: "",
          imageQuestionFile: null,
          imageQuestionUrl: null,
        };
      }

      if (field === "sound_question") {
        return {
          ...item,
          audioQuestionFileName: "",
          audioQuestionFile: null,
          audioQuestionUrl: null,
        };
      }

      if (field === "image_hint") {
        return {
          ...item,
          imageHintFileName: "",
          imageHintFile: null,
          imageHintUrl: null,
        };
      }

      // sound_hint
      return {
        ...item,
        audioHintFileName: "",
        audioHintFile: null,
        audioHintUrl: null,
      };
    });
  };

  const confirmDeleteMediaField = async () => {
    if (!pendingMediaDelete) return;

    const { itemId, field } = pendingMediaDelete;
    const shouldCallDeleteApi =
      isUpdateMode && initialItemIdsRef.current.has(itemId);

    try {
      setIsDeletingMedia(true);

      if (shouldCallDeleteApi) {
        const response =
          await completeTheSentenceAPI.hardDeleteMediaFileAndHint({
            id: itemId,
            field,
          });

        if (response?.status === "failed") {
          setStatusPopUp({
            title: tCompleteTheSentence("error"),
            message: tCompleteTheSentence("deleteFieldError", {
              field: getDeleteFieldLabel(field),
            }),
            type: "error",
          });
          return;
        }

        patchSavedCompleteTheSentenceItemsSnapshotAfterMediaDeleted(
          savedFormSnapshotRef,
          itemId,
          field,
        );
      }

      clearMediaFieldFromItem(itemId, field);
      setPendingMediaDelete(null);
    } finally {
      setIsDeletingMedia(false);
    }
  };

  const collectUploadTargets = (sourceItems: CompleteTheSentenceItem[]) => {
    const targets: UploadTarget[] = [];

    sourceItems.forEach((item, index) => {
      const timestamp = Date.now();

      if (item.imageQuestionFile && !item.imageQuestionUrl) {
        const ext = getFileExtension(item.imageQuestionFile.name);
        targets.push({
          key: `cts-${index}-image_question_answer`,
          file: item.imageQuestionFile,
          fileName: `image_question_answer_cts${item.id}_${timestamp}${ext}`,
        });
      }

      if (item.audioQuestionFile && !item.audioQuestionUrl) {
        const ext = getFileExtension(item.audioQuestionFile.name);
        targets.push({
          key: `cts-${index}-sound_question_answer`,
          file: item.audioQuestionFile,
          fileName: `sound_question_answer_cts${item.id}_${timestamp}${ext}`,
        });
      }

      if (item.imageHintFile && !item.imageHintUrl) {
        const ext = getFileExtension(item.imageHintFile.name);
        targets.push({
          key: `cts-${index}-image_hint`,
          file: item.imageHintFile,
          fileName: `image_hint_cts${item.id}_${timestamp}${ext}`,
        });
      }

      if (item.audioHintFile && !item.audioHintUrl) {
        const ext = getFileExtension(item.audioHintFile.name);
        targets.push({
          key: `cts-${index}-sound_hint`,
          file: item.audioHintFile,
          fileName: `sound_hint_cts${item.id}_${timestamp}${ext}`,
        });
      }

      item.segments.forEach((segment, blankIndex) => {
        const correctAnswerSoundFile = segment.correctAnswerSoundFile;
        if (correctAnswerSoundFile) {
          const ext = getFileExtension(correctAnswerSoundFile.name);
          targets.push({
            key: createCorrectAnswerSoundUploadKey(item.id, blankIndex),
            file: correctAnswerSoundFile,
            fileName: `answer_sound_cts${item.id}_${blankIndex}_${timestamp}${ext}`,
          });
        }

        const correctAnswerImageFile = segment.correctAnswerImageFile;
        if (correctAnswerImageFile) {
          const ext = getFileExtension(correctAnswerImageFile.name);
          targets.push({
            key: createCorrectAnswerImageUploadKey(item.id, blankIndex),
            file: correctAnswerImageFile,
            fileName: `answer_image_cts${item.id}_${blankIndex}_${timestamp}${ext}`,
          });
        }
      });

      item.wrongChoices.forEach((wrongChoice, wrongIndex) => {
        const wrongChoiceSoundFile = wrongChoice.soundFile;
        if (wrongChoiceSoundFile) {
          const ext = getFileExtension(wrongChoiceSoundFile.name);
          targets.push({
            key: createWrongAnswerSoundUploadKey(item.id, wrongIndex),
            file: wrongChoiceSoundFile,
            fileName: `wrong_answer_sound_cts${item.id}_${wrongIndex}_${timestamp}${ext}`,
          });
        }

        const wrongChoiceImageFile = wrongChoice.imageFile;
        if (wrongChoiceImageFile) {
          const ext = getFileExtension(wrongChoiceImageFile.name);
          targets.push({
            key: createWrongAnswerImageUploadKey(item.id, wrongIndex),
            file: wrongChoiceImageFile,
            fileName: `wrong_answer_image_cts${item.id}_${wrongIndex}_${timestamp}${ext}`,
          });
        }
      });
    });

    return targets;
  };

  const uploadPendingMedia = async (targets: UploadTarget[]) => {
    const uploadedUrlByKey = new Map<string, string>();

    if (targets.length === 0) {
      return uploadedUrlByKey;
    }

    const uploadResults = await Promise.allSettled(
      targets.map((target) =>
        uploadAPI
          .uploadMedia({
            file: target.file,
            fileName: target.fileName,
          })
          .then((response) => ({ target, response })),
      ),
    );

    for (const result of uploadResults) {
      if (result.status === "rejected") {
        throw new Error("upload_failed");
      }

      const response = result.value.response as {
        status?: string;
        url?: string;
      };
      if (response?.status === "failed" || !response?.url) {
        throw new Error("upload_failed");
      }

      uploadedUrlByKey.set(result.value.target.key, response.url);
    }

    return uploadedUrlByKey;
  };

  const applyUploadedUrlsToState = (
    sourceItems: CompleteTheSentenceItem[],
    uploadedUrlByKey: Map<string, string>,
  ) => {
    return sourceItems.map((item, index) => ({
      ...item,
      imageQuestionUrl:
        uploadedUrlByKey.get(`cts-${index}-image_question_answer`) ??
        item.imageQuestionUrl,
      imageQuestionFile: uploadedUrlByKey.get(
        `cts-${index}-image_question_answer`,
      )
        ? null
        : item.imageQuestionFile,
      audioQuestionUrl:
        uploadedUrlByKey.get(`cts-${index}-sound_question_answer`) ??
        item.audioQuestionUrl,
      audioQuestionFile: uploadedUrlByKey.get(
        `cts-${index}-sound_question_answer`,
      )
        ? null
        : item.audioQuestionFile,
      imageHintUrl:
        uploadedUrlByKey.get(`cts-${index}-image_hint`) ?? item.imageHintUrl,
      imageHintFile: uploadedUrlByKey.get(`cts-${index}-image_hint`)
        ? null
        : item.imageHintFile,
      audioHintUrl:
        uploadedUrlByKey.get(`cts-${index}-sound_hint`) ?? item.audioHintUrl,
      audioHintFile: uploadedUrlByKey.get(`cts-${index}-sound_hint`)
        ? null
        : item.audioHintFile,
      segments: item.segments.map((segment, blankIndex) => ({
        ...segment,
        correctAnswerSound:
          uploadedUrlByKey.get(
            createCorrectAnswerSoundUploadKey(item.id, blankIndex),
          ) ?? segment.correctAnswerSound,
        correctAnswerSoundFile: uploadedUrlByKey.get(
          createCorrectAnswerSoundUploadKey(item.id, blankIndex),
        )
          ? null
          : segment.correctAnswerSoundFile,
        correctAnswerImage:
          uploadedUrlByKey.get(
            createCorrectAnswerImageUploadKey(item.id, blankIndex),
          ) ?? segment.correctAnswerImage,
        correctAnswerImageFile: uploadedUrlByKey.get(
          createCorrectAnswerImageUploadKey(item.id, blankIndex),
        )
          ? null
          : segment.correctAnswerImageFile,
      })),
      wrongChoices: item.wrongChoices.map((wrongChoice, wrongIndex) => ({
        ...wrongChoice,
        sound:
          uploadedUrlByKey.get(
            createWrongAnswerSoundUploadKey(item.id, wrongIndex),
          ) ?? wrongChoice.sound,
        soundFile: uploadedUrlByKey.get(
          createWrongAnswerSoundUploadKey(item.id, wrongIndex),
        )
          ? null
          : wrongChoice.soundFile,
        image:
          uploadedUrlByKey.get(
            createWrongAnswerImageUploadKey(item.id, wrongIndex),
          ) ?? wrongChoice.image,
        imageFile: uploadedUrlByKey.get(
          createWrongAnswerImageUploadKey(item.id, wrongIndex),
        )
          ? null
          : wrongChoice.imageFile,
      })),
    }));
  };

  const refreshFromServer = async () => {
    if (!previewUuid) {
      return;
    }

    const latestGameDetail =
      await mainAPI.getGameinfoAndQuestionByUuid(previewUuid);
    if (
      latestGameDetail &&
      (latestGameDetail as { status?: string }).status !== "failed"
    ) {
      applyLoadedGameDetail(
        latestGameDetail as CompleteTheSentenceProps["initialGameDetail"],
      );
    }
  };

  const buildRowsPayload = (
    sourceItems: CompleteTheSentenceItem[],
    expectedTimeValue: number,
  ) => {
    return sourceItems.map((item) => ({
      no: item.no,
      expected_time: expectedTimeValue,
      question_answer: buildQuestionAnswerPayload(item),
      hint: item.hasHint ? item.hintText.trim() || null : null,
      image_question_answer: item.imageQuestionUrl || null,
      sound_question_answer: item.audioQuestionUrl || null,
      image_hint: item.hasHint ? item.imageHintUrl || null : null,
      sound_hint: item.hasHint ? item.audioHintUrl || null : null,
    }));
  };

  const handleSubmit = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tCompleteTheSentence("error"),
        message: tCompleteTheSentence("noRecentGameData"),
        type: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedExerciseName = exerciseName.trim();
      const trimmedSuggestion = suggestion.trim();
      const trimmedDescription = description.trim();
      const trimmedExpectedTime = expectedTime.trim();
      const expectedTimeValue = Number(trimmedExpectedTime);
      if (
        trimmedExpectedTime === "" ||
        !Number.isInteger(expectedTimeValue) ||
        expectedTimeValue < 0
      ) {
        setStatusPopUp({
          title: tCompleteTheSentence("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
        return;
      }
      const shouldUpdateExerciseName =
        trimmedExerciseName !== initialExerciseNameRef.current.trim();
      const shouldUpdateSuggestion =
        trimmedSuggestion !== initialSuggestionRef.current.trim();
      const shouldUpdateDescription =
        trimmedDescription !== initialDescriptionRef.current.trim();

      if (
        shouldUpdateExerciseName ||
        shouldUpdateSuggestion ||
        shouldUpdateDescription
      ) {
        const updateResponse = await mainAPI.updateExerciseNameAndSuggestion({
          gameId: createdGameData.id,
          ...(shouldUpdateExerciseName
            ? { exercise_name: trimmedExerciseName }
            : {}),
          ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
          ...(shouldUpdateDescription
            ? { description: trimmedDescription }
            : {}),
        });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tCompleteTheSentence("error"),
            message: tCompleteTheSentence("updateError"),
            type: "error",
          });
          return;
        }

        initialExerciseNameRef.current = trimmedExerciseName;
        initialSuggestionRef.current = trimmedSuggestion;
        initialDescriptionRef.current = trimmedDescription;
        setExerciseName(trimmedExerciseName);
        setSuggestion(trimmedSuggestion);
        setDescription(trimmedDescription);
      }

      const uploadTargets = collectUploadTargets(items);
      const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
      const itemsWithResolvedUrls = applyUploadedUrlsToState(
        items,
        uploadedUrlByKey,
      );

      if (
        !Array.isArray(itemsWithResolvedUrls) ||
        itemsWithResolvedUrls.length === 0
      ) {
        setStatusPopUp({
          title: tCompleteTheSentence("error"),
          message: tCompleteTheSentence("minMatchItems"),
          type: "error",
        });
        return;
      }

      const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
      if (!normalizedGameSkills) {
        setStatusPopUp({
          title: tCompleteTheSentence("error"),
          message: tCompleteTheSentence("invalidCompetencyData"),
          type: "error",
        });
        return;
      }

      const normalizedGameAudios = normalizeGameAudiosOrNull(gameAudios);
      if (!normalizedGameAudios) {
        setStatusPopUp({
          title: tCompleteTheSentence("error"),
          message: tCompleteTheSentence("invalidAudioData"),
          type: "error",
        });
        return;
      }

      for (let index = 0; index < itemsWithResolvedUrls.length; index += 1) {
        const item = itemsWithResolvedUrls[index];
        const no = index + 1;

        const blankCount = countBlanksFromSentence(item.sentenceText);
        if (blankCount === 0) {
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tCompleteTheSentence("error"),
              tCompleteTheSentence("blankRequiredByOrder", { no }),
              completeSentenceFieldTarget(item.id, "sentence"),
            ),
          );
          return;
        }

        for (
          let blankIndex = 0;
          blankIndex < item.segments.length;
          blankIndex += 1
        ) {
          if (!item.segments[blankIndex].correctAnswer.trim()) {
            openStatusPopUp(
              setStatusPopUp,
              createGameFieldErrorPopUp(
                tCompleteTheSentence("error"),
                tCompleteTheSentence("correctAnswerRequiredByOrderAndBlank", {
                  no,
                  blank: blankIndex + 1,
                }),
                completeSentenceFieldTarget(
                  item.id,
                  "blank-correct",
                  blankIndex,
                ),
              ),
            );
            return;
          }

          if (item.wrongChoices.length > 99) {
            openStatusPopUp(
              setStatusPopUp,
              createGameFieldErrorPopUp(
                tCompleteTheSentence("error"),
                tCompleteTheSentence("maxWrongAnswersByOrder", {
                  no,
                  count: 99,
                }),
                completeSentenceFieldTarget(item.id, "blank-wrong"),
              ),
            );
            return;
          }
        }
      }

      const response = await completeTheSentenceAPI.createByGame({
        gameId: createdGameData.id,
        skill_listening_speaking: normalizedGameSkills.skill_listening_speaking,
        skill_lang_nature: normalizedGameSkills.skill_lang_nature,
        skill_reading: normalizedGameSkills.skill_reading,
        skill_writing: normalizedGameSkills.skill_writing,
        audio_question: normalizedGameAudios.audio_question,
        audio_choice: normalizedGameAudios.audio_choice,
        audio_assistant_voice: normalizedGameAudios.audio_assistant_voice,
        audio_background: normalizedGameAudios.audio_background,
        complete_the_sentences: buildRowsPayload(
          itemsWithResolvedUrls,
          expectedTimeValue,
        ),
      });

      if (response?.status === "failed") {
        setStatusPopUp({
          title: tCompleteTheSentence("error"),
          message: tCompleteTheSentence("saveError"),
          type: "error",
        });
        return;
      }

      setItems(setNoByOrder(itemsWithResolvedUrls));
      initialGameSkillsRef.current = normalizedGameSkills;
      initialGameAudiosRef.current = normalizedGameAudios;
      await refreshFromServer();
      setIsUpdateMode(true);
      setStatusPopUp(
        buildGameSuccessStatus(
          tCompleteTheSentence("completeTheSentences"),
          "create",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tCompleteTheSentence("error"),
        message: tCompleteTheSentence("noGameDataToUpdate"),
        type: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedExerciseName = exerciseName.trim();
      const trimmedSuggestion = suggestion.trim();
      const trimmedDescription = description.trim();
      const trimmedExpectedTime = expectedTime.trim();
      const expectedTimeValue = Number(trimmedExpectedTime);
      if (
        trimmedExpectedTime === "" ||
        !Number.isInteger(expectedTimeValue) ||
        expectedTimeValue < 0
      ) {
        setStatusPopUp({
          title: tCompleteTheSentence("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
        return;
      }
      const shouldUpdateExerciseName =
        trimmedExerciseName !== initialExerciseNameRef.current.trim();
      const shouldUpdateSuggestion =
        trimmedSuggestion !== initialSuggestionRef.current.trim();
      const shouldUpdateDescription =
        trimmedDescription !== initialDescriptionRef.current.trim();

      if (
        shouldUpdateExerciseName ||
        shouldUpdateSuggestion ||
        shouldUpdateDescription
      ) {
        const updateResponse = await mainAPI.updateExerciseNameAndSuggestion({
          gameId: createdGameData.id,
          ...(shouldUpdateExerciseName
            ? { exercise_name: trimmedExerciseName }
            : {}),
          ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
          ...(shouldUpdateDescription
            ? { description: trimmedDescription }
            : {}),
        });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tCompleteTheSentence("error"),
            message: tCompleteTheSentence("updateError"),
            type: "error",
          });
          return;
        }

        initialExerciseNameRef.current = trimmedExerciseName;
        initialSuggestionRef.current = trimmedSuggestion;
        initialDescriptionRef.current = trimmedDescription;
        setExerciseName(trimmedExerciseName);
        setSuggestion(trimmedSuggestion);
        setDescription(trimmedDescription);
      }

      const uploadTargets = collectUploadTargets(items);
      const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
      const itemsWithResolvedUrls = applyUploadedUrlsToState(
        items,
        uploadedUrlByKey,
      );

      const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
      if (!normalizedGameSkills) {
        setStatusPopUp({
          title: tCompleteTheSentence("error"),
          message: tCompleteTheSentence("invalidCompetencyData"),
          type: "error",
        });
        return;
      }

      const normalizedGameAudios = normalizeGameAudiosOrNull(gameAudios);
      if (!normalizedGameAudios) {
        setStatusPopUp({
          title: tCompleteTheSentence("error"),
          message: tCompleteTheSentence("invalidAudioData"),
          type: "error",
        });
        return;
      }

      for (let index = 0; index < itemsWithResolvedUrls.length; index += 1) {
        const item = itemsWithResolvedUrls[index];
        const no = index + 1;

        const blankCount = countBlanksFromSentence(item.sentenceText);
        if (blankCount === 0) {
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tCompleteTheSentence("error"),
              tCompleteTheSentence("blankRequiredByOrder", { no }),
              completeSentenceFieldTarget(item.id, "sentence"),
            ),
          );
          return;
        }

        for (
          let blankIndex = 0;
          blankIndex < item.segments.length;
          blankIndex += 1
        ) {
          if (!item.segments[blankIndex].correctAnswer.trim()) {
            openStatusPopUp(
              setStatusPopUp,
              createGameFieldErrorPopUp(
                tCompleteTheSentence("error"),
                tCompleteTheSentence("correctAnswerRequiredByOrderAndBlank", {
                  no,
                  blank: blankIndex + 1,
                }),
                completeSentenceFieldTarget(
                  item.id,
                  "blank-correct",
                  blankIndex,
                ),
              ),
            );
            return;
          }

          if (item.wrongChoices.length > 99) {
            openStatusPopUp(
              setStatusPopUp,
              createGameFieldErrorPopUp(
                tCompleteTheSentence("error"),
                tCompleteTheSentence("maxWrongAnswersByOrder", {
                  no,
                  count: 99,
                }),
                completeSentenceFieldTarget(item.id, "blank-wrong"),
              ),
            );
            return;
          }
        }
      }

      const changedSkillPayload: Partial<GameSkills> = {};
      if (
        normalizedGameSkills.skill_listening_speaking !==
        initialGameSkillsRef.current.skill_listening_speaking
      ) {
        changedSkillPayload.skill_listening_speaking =
          normalizedGameSkills.skill_listening_speaking;
      }
      if (
        normalizedGameSkills.skill_lang_nature !==
        initialGameSkillsRef.current.skill_lang_nature
      ) {
        changedSkillPayload.skill_lang_nature =
          normalizedGameSkills.skill_lang_nature;
      }
      if (
        normalizedGameSkills.skill_reading !==
        initialGameSkillsRef.current.skill_reading
      ) {
        changedSkillPayload.skill_reading = normalizedGameSkills.skill_reading;
      }
      if (
        normalizedGameSkills.skill_writing !==
        initialGameSkillsRef.current.skill_writing
      ) {
        changedSkillPayload.skill_writing = normalizedGameSkills.skill_writing;
      }

      const changedAudioPayload: Partial<GameAudios> = {};
      if (
        normalizedGameAudios.audio_question !==
        initialGameAudiosRef.current.audio_question
      ) {
        changedAudioPayload.audio_question =
          normalizedGameAudios.audio_question;
      }
      if (
        normalizedGameAudios.audio_choice !==
        initialGameAudiosRef.current.audio_choice
      ) {
        changedAudioPayload.audio_choice = normalizedGameAudios.audio_choice;
      }
      if (
        normalizedGameAudios.audio_assistant_voice !==
        initialGameAudiosRef.current.audio_assistant_voice
      ) {
        changedAudioPayload.audio_assistant_voice =
          normalizedGameAudios.audio_assistant_voice;
      }
      if (
        normalizedGameAudios.audio_background !==
        initialGameAudiosRef.current.audio_background
      ) {
        changedAudioPayload.audio_background =
          normalizedGameAudios.audio_background;
      }

      const currentIds = new Set<number>(
        itemsWithResolvedUrls.map((item) => item.id),
      );
      const deletedIds = Array.from(initialItemIdsRef.current).filter(
        (id) => !currentIds.has(id),
      );

      for (const deletedId of deletedIds) {
        const deleteResponse = await completeTheSentenceAPI.hardDelete({
          id: deletedId,
        });
        if (deleteResponse?.status === "failed") {
          setStatusPopUp({
            title: tCompleteTheSentence("error"),
            message: tCompleteTheSentence("deleteItemByIdError", {
              id: deletedId,
            }),
            type: "error",
          });
          return;
        }
      }

      const existingRows = itemsWithResolvedUrls.filter((item) =>
        initialItemIdsRef.current.has(item.id),
      );
      const newRows = itemsWithResolvedUrls.filter(
        (item) => !initialItemIdsRef.current.has(item.id),
      );

      for (const row of existingRows) {
        const updateResponse = await completeTheSentenceAPI.update({
          id: row.id,
          no: row.no,
          expected_time: expectedTimeValue,
          question_answer: buildQuestionAnswerPayload(row),
          hint: row.hasHint ? row.hintText.trim() || null : null,
          image_question_answer: row.imageQuestionUrl || null,
          sound_question_answer: row.audioQuestionUrl || null,
          image_hint: row.hasHint ? row.imageHintUrl || null : null,
          sound_hint: row.hasHint ? row.audioHintUrl || null : null,
          ...changedSkillPayload,
          ...changedAudioPayload,
        });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tCompleteTheSentence("error"),
            message: tCompleteTheSentence("updateItemByOrderError", {
              no: row.no,
            }),
            type: "error",
          });
          return;
        }
      }

      if (newRows.length > 0) {
        const createNewRowsResponse = await completeTheSentenceAPI.createByGame(
          {
            gameId: createdGameData.id,
            skill_listening_speaking:
              normalizedGameSkills.skill_listening_speaking,
            skill_lang_nature: normalizedGameSkills.skill_lang_nature,
            skill_reading: normalizedGameSkills.skill_reading,
            skill_writing: normalizedGameSkills.skill_writing,
            audio_question: normalizedGameAudios.audio_question,
            audio_choice: normalizedGameAudios.audio_choice,
            audio_assistant_voice: normalizedGameAudios.audio_assistant_voice,
            audio_background: normalizedGameAudios.audio_background,
            complete_the_sentences: buildRowsPayload(
              newRows,
              expectedTimeValue,
            ),
          },
        );

        if (createNewRowsResponse?.status === "failed") {
          setStatusPopUp({
            title: tCompleteTheSentence("error"),
            message: tCompleteTheSentence("addQuestionError"),
            type: "error",
          });
          return;
        }
      }

      setItems(setNoByOrder(itemsWithResolvedUrls));
      await refreshFromServer();

      initialGameSkillsRef.current = normalizedGameSkills;
      initialGameAudiosRef.current = normalizedGameAudios;
      setIsUpdateMode(true);
      setStatusPopUp(
        buildGameSuccessStatus(
          tCompleteTheSentence("completeTheSentences"),
          "update",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseStatusPopUp = () => {
    closeStatusPopUp(setStatusPopUp, () => {
      void onCompleted;
    });
  };

  const handleStatusButtonClick = () => {
    if (isStatusConfirming || isSubmitting) {
      return;
    }

    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tCompleteTheSentence("updateStatusError"),
        message: tCompleteTheSentence("noGameDataToChangeStatus"),
        type: "error",
      });
      return;
    }

    setIsStatusConfirmOpen(true);
  };

  const closeStatusConfirm = () => {
    if (isStatusConfirming) {
      return;
    }
    setIsStatusConfirmOpen(false);
  };

  const confirmStatusToggle = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tCompleteTheSentence("updateStatusError"),
        message: tCompleteTheSentence("noGameDataToChangeStatus"),
        type: "error",
      });
      setIsStatusConfirmOpen(false);
      return;
    }

    const nextStatus = !isPublished;
    const dialogContent = getStatusToggleDialogContent(isPublished, {
      confirmPublish: tCompleteTheSentence("confirmPublish"),
      publishPrompt: tCompleteTheSentence("publishPrompt"),
      published: tCompleteTheSentence("published"),
      publishedMessage: tCompleteTheSentence("publishedMessage"),
      confirmUnpublish: tCompleteTheSentence("confirmUnpublish"),
      unpublishPrompt: tCompleteTheSentence("unpublishPrompt"),
      unpublished: tCompleteTheSentence("unpublished"),
      unpublishedMessage: tCompleteTheSentence("unpublishedMessage"),
    });

    setIsStatusConfirming(true);
    const response = await mainAPI.updateGameStatus({
      id: createdGameData.id,
      status: nextStatus,
    });
    setIsStatusConfirming(false);
    setIsStatusConfirmOpen(false);

    if (!response || response.status === "failed") {
      setStatusPopUp({
        title: tCompleteTheSentence("updateStatusError"),
        message: tCompleteTheSentence("changeStatusError"),
        type: "error",
      });
      return;
    }

    setIsPublished(nextStatus);
    setStatusPopUp({
      title: dialogContent.successTitle,
      message: dialogContent.successMessage,
      type: "success",
    });
  };

  const isExpectedTimeValid = useMemo(() => {
    const trimmed = expectedTime.trim();
    if (trimmed === "") {
      return false;
    }
    const parsed = Number(trimmed);
    return Number.isInteger(parsed) && parsed >= 0;
  }, [expectedTime]);
  const expectedTimeDisplay = useMemo(() => {
    if (!isExpectedTimeValid) {
      return "";
    }
    const value = Number(expectedTime.trim());
    if (value > 59) {
      const minutes = value / 60;
      const minuteText = Number.isInteger(minutes)
        ? String(minutes)
        : minutes.toFixed(2).replace(/\.?0+$/, "");
      return `${minuteText} ${tMain("expectedTimeMinuteUnit")}`;
    }
    return `${value} ${tMain("expectedTimeSecondUnit")}`;
  }, [expectedTime, isExpectedTimeValid, tMain]);
  const isSaveReady = useMemo(
    () => areCompleteTheSentenceItemsSaveReady(items) && isExpectedTimeValid,
    [items, isExpectedTimeValid],
  );

  const currentFormSnapshot = useMemo(
    () =>
      createCompleteTheSentenceItemsGameSnapshot(
        exerciseName,
        suggestion,
        description,
        gameSkills,
        gameAudios,
        toSnapshotItems(items),
      ),
    [exerciseName, suggestion, description, gameSkills, gameAudios, items],
  );
  const hasUnsavedChanges = useHasUnsavedGameFormChanges(
    isUpdateMode,
    currentFormSnapshot,
    savedFormSnapshotRef,
  );
  useReportGameEditorLeaveGuard(
    getShouldConfirmLeaveGameEditor(isUpdateMode, hasUnsavedChanges),
  );
  const canPreview = getCanGamePreview(
    isUpdateMode,
    previewUuid,
    hasUnsavedChanges,
  );

  const handleClickFinishButton = () => {
    if (isSubmitting || !isSaveReady) {
      return;
    }
    setIsConfirmSaveOpen(true);
  };

  const handleConfirmSave = () => {
    setIsConfirmSaveOpen(false);
    if (isUpdateMode) {
      void handleUpdate();
      return;
    }
    void handleSubmit();
  };

  const handleOpenPreview = () => {
    if (!canPreview || hasUnsavedChanges || !isExpectedTimeValid) {
      if (!isExpectedTimeValid) {
        setStatusPopUp({
          title: tCompleteTheSentence("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
      } else if (hasUnsavedChanges) {
        setStatusPopUp({
          title: PREVIEW_UNSAVED_TITLE,
          message: PREVIEW_UNSAVED_MESSAGE,
          type: "info",
        });
      }
      return;
    }

    if (!previewUuid) {
      setStatusPopUp({
        title: tCompleteTheSentence("previewError"),
        message: tCompleteTheSentence("noGameUUIDForPreview"),
        type: "error",
      });
      return;
    }

    const previewBaseUrl =
      process.env.NEXT_PUBLIC_BASE_GAME_URL ||
      "https://thaipilagame.gforcesolution.com";
    const normalizedBaseUrl = previewBaseUrl.replace(/\/$/, "");
    const previewUrl = `${normalizedBaseUrl}/${encodeURIComponent(previewUuid)}?preview`;
    setPreviewIframeUrl(previewUrl);
  };

  return (
    <section className="mx-auto w-full rounded px-4 py-3 text-[#3f3f3f] lg:px-10">
      <div className="border-b border-[#dddddd] pb-2 text-2xl leading-none">
        <span className="font-semibold text-[#4E4E4E]">
          {tCompleteTheSentence("editContent")} &gt;{" "}
        </span>
        <span className="font-bold text-[#2b2b2b]">
          {tCompleteTheSentence("completeTheSentences")}
        </span>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">
            {tCompleteTheSentence("activityName")}
          </label>
          <input
            value={exerciseName}
            onChange={(event) => setExerciseName(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tCompleteTheSentence("noActivityName")}
          />
        </div>

        <div>
          <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
            {tCompleteTheSentence("details")}
          </label>
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tCompleteTheSentence("fillInTheBlank")}
          />
        </div>

        <div>
          <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
            {tCompleteTheSentence("questionTitle")}
          </label>
          <input
            value={suggestion}
            onChange={(event) => setSuggestion(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            // placeholder={tCompleteTheSentence("fillInTheBlank")}
          />
        </div>

        <div className="flex items-center gap-3">
          <span className="text-lg font-medium text-[#5b5b5b]">
            {tCompleteTheSentence("status")}:
          </span>
          <button
            type="button"
            onClick={handleStatusButtonClick}
            disabled={
              !createdGameData?.id || isSubmitting || isStatusConfirming
            }
            className={getPublishButtonClass(isPublished)}
          >
            {renderPublishStatusContent(
              isPublished,
              {
                publish: tCompleteTheSentence("publish"),
                unpublish: tCompleteTheSentence("unpublish"),
              },
              16,
            )}
          </button>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {items.map((item, index) => {
          const questionImageId = `question-image-${item.id}`;
          const questionAudioId = `question-audio-${item.id}`;
          const hintImageId = `hint-image-${item.id}`;
          const hintAudioId = `hint-audio-${item.id}`;

          return (
            <div
              key={item.id}
              className="rounded-lg border border-[#e5e5e5] bg-[#fafafa] p-5 shadow-md"
            >
              <div className="mb-2 flex items-center gap-2">
                <span className="w-6 shrink-0" aria-hidden="true" />
                <span className="min-w-0 flex-1 text-base font-semibold text-[#444]">
                  {tCompleteTheSentence("questionAt", { no: index + 1 })}
                </span>
                <span className="flex w-5 shrink-0 justify-center text-xs font-medium text-[#888]">
                  {tCompleteTheSentence("swap")}
                </span>
              </div>

              <div className="flex items-start gap-2">
                <span className="w-6 shrink-0 pt-2 text-right text-lg font-semibold text-[#5a5a5a]">
                  {index + 1}.
                </span>
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <div
                    id={
                      completeSentenceFieldTarget(item.id, "sentence").scrollId
                    }
                    className={`flex h-9.5 min-w-0 flex-1 items-center rounded-md border bg-white px-3 focus-within:border-blue-400 ${getFieldBorderClass(completeSentenceFieldTarget(item.id, "sentence").highlightKey)}`}
                  >
                    <input
                      value={item.sentenceText}
                      onChange={(event) =>
                        updateSentenceText(item.id, event.target.value)
                      }
                      className="h-full w-full min-w-0 bg-transparent text-base outline-none"
                    />
                    <div className="flex items-center gap-2">
                      <MediaEditGroup
                        imageInputId={questionImageId}
                        imageFile={item.imageQuestionFile}
                        imageUrl={item.imageQuestionUrl}
                        imageAlt={tCompleteTheSentence("questionImageAlt")}
                        onImageChange={(file) =>
                          setFileOnItem(item.id, {
                            file,
                            fileNameField: "imageQuestionFileName",
                            fileField: "imageQuestionFile",
                            urlField: "imageQuestionUrl",
                          })
                        }
                        audioInputId={questionAudioId}
                        audioFile={item.audioQuestionFile}
                        audioUrl={item.audioQuestionUrl}
                        onAudioChange={(file) =>
                          setFileOnItem(item.id, {
                            file,
                            fileNameField: "audioQuestionFileName",
                            fileField: "audioQuestionFile",
                            urlField: "audioQuestionUrl",
                          })
                        }
                        onImageDelete={() =>
                          requestDeleteMediaField(item.id, "image_question")
                        }
                        onAudioDelete={() =>
                          requestDeleteMediaField(item.id, "sound_question")
                        }
                        disableDelete={isSubmitting || isDeletingMedia}
                      />
                    </div>
                  </div>
                  <div className="flex w-5 shrink-0 flex-col items-center gap-0 text-[#555]">
                    <button
                      type="button"
                      onClick={() => moveItem(item.id, -1)}
                      disabled={index === 0}
                      className="cursor-pointer leading-none hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                      title={tCompleteTheSentence("moveUp")}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(item.id, 1)}
                      disabled={index === items.length - 1}
                      className="cursor-pointer leading-none hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                      title={tCompleteTheSentence("moveDown")}
                    >
                      ↓
                    </button>
                  </div>
                </div>
              </div>
              <p className="mt-1.5 pl-8 text-sm text-[#888]">
                {tCompleteTheSentence("underscoreInstruction")}
              </p>

              <div className="mt-4 grid grid-cols-1 items-start gap-4 md:grid-cols-2">
                <div className="overflow-hidden rounded-lg border border-[#e0e0e0] bg-white">
                  <div className="flex items-center gap-2 bg-[#e8f8ea] px-3 py-2.5">
                    <span className="text-[#15B427]">
                      <AiOutlineCheckCircle size={21} />
                    </span>
                    <span className="text-sm font-semibold text-[#15B427]">
                      {tCompleteTheSentence("correctAnswer")}
                    </span>
                  </div>
                  <div className="space-y-2 bg-white p-3">
                    {item.segments.map((segment, blankIndex) => (
                      <div
                        key={`${item.id}-blank-${blankIndex}`}
                        className="flex items-center gap-2"
                      >
                        <span className="w-6 shrink-0 text-right text-sm font-semibold text-[#5a5a5a]">
                          {blankIndex + 1}.
                        </span>
                        <div
                          id={
                            completeSentenceFieldTarget(
                              item.id,
                              "blank-correct",
                              blankIndex,
                            ).scrollId
                          }
                          className={`flex h-9.5 min-w-0 flex-1 items-center rounded-md border bg-white px-3 focus-within:border-blue-400 ${getFieldBorderClass(completeSentenceFieldTarget(item.id, "blank-correct", blankIndex).highlightKey)}`}
                        >
                          <input
                            value={segment.correctAnswer}
                            onChange={(event) =>
                              updateBlankCorrectAnswer(
                                item.id,
                                blankIndex,
                                event.target.value,
                              )
                            }
                            className="w-full min-w-0 bg-transparent text-sm outline-none"
                          />
                          <MediaEditGroup
                            imageInputId={`correct-answer-image-${item.id}-${blankIndex}`}
                            imageFile={segment.correctAnswerImageFile}
                            imageUrl={segment.correctAnswerImage}
                            imageAlt={tCompleteTheSentence(
                              "correctAnswerImageAltByBlank",
                              { no: blankIndex + 1 },
                            )}
                            onImageChange={(file) =>
                              updateCorrectAnswerImage(
                                item.id,
                                blankIndex,
                                file,
                              )
                            }
                            onImageDelete={() =>
                              removeCorrectAnswerImage(item.id, blankIndex)
                            }
                            audioInputId={`correct-answer-sound-${item.id}-${blankIndex}`}
                            audioFile={segment.correctAnswerSoundFile}
                            audioUrl={segment.correctAnswerSound}
                            onAudioChange={(file) =>
                              updateCorrectAnswerSound(
                                item.id,
                                blankIndex,
                                file,
                              )
                            }
                            onAudioDelete={() =>
                              removeCorrectAnswerSound(item.id, blankIndex)
                            }
                            disableDelete={isSubmitting}
                            size="sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            removeBlankFromSentence(item.id, blankIndex)
                          }
                          className={`shrink-0 ${MEDIA_BTN_DELETE_SM}`}
                          title={tCompleteTheSentence("deleteCorrectAnswer")}
                        >
                          <MdDeleteOutline size={16} />
                        </button>
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <span className="w-6 shrink-0" aria-hidden="true" />
                      <button
                        type="button"
                        onClick={() => addBlankToSentence(item.id)}
                        className="inline-flex w-fit items-center gap-1 rounded-md border border-[#9dd4b0] bg-[#F7FBF8] px-3 py-1.5 text-sm font-semibold text-[#15B427] transition-colors hover:bg-[#e8f8ea] cursor-pointer"
                      >
                        {tCompleteTheSentence("addCorrectAnswer")}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-lg border border-[#e0e0e0] bg-white">
                  <div className="flex shrink-0 items-center gap-2 bg-[#fff5f5] px-3 py-2.5">
                    <span className="text-[#e02020]">
                      <AiOutlineCloseCircle size={21} />
                    </span>
                    <span className="text-sm font-semibold text-[#e02020]">
                      {tCompleteTheSentence("wrongAnswer")}
                    </span>
                  </div>
                  <div
                    id={
                      completeSentenceFieldTarget(item.id, "blank-wrong")
                        .scrollId
                    }
                    className="space-y-2 bg-white p-3"
                  >
                    {item.wrongChoices.map((wrongChoice, wrongIndex) => (
                      <div
                        key={`${item.id}-wrong-${wrongIndex}`}
                        className="flex items-center gap-2"
                      >
                        <span className="w-6 shrink-0 text-right text-sm font-semibold text-[#5a5a5a]">
                          {wrongIndex + 1}.
                        </span>
                        <div
                          className={`flex h-9.5 min-w-0 flex-1 items-center rounded-md border bg-white px-3 focus-within:border-blue-400 ${getFieldBorderClass(completeSentenceFieldTarget(item.id, "blank-wrong", wrongIndex).highlightKey)}`}
                        >
                          <input
                            value={wrongChoice.text}
                            onChange={(event) =>
                              updateWrongChoiceText(
                                item.id,
                                wrongIndex,
                                event.target.value,
                              )
                            }
                            className="w-full min-w-0 bg-transparent text-sm outline-none"
                          />
                          <MediaEditGroup
                            imageInputId={`wrong-answer-image-${item.id}-${wrongIndex}`}
                            imageFile={wrongChoice.imageFile}
                            imageUrl={wrongChoice.image}
                            imageAlt={tCompleteTheSentence(
                              "wrongAnswerImageAltByNo",
                              { no: wrongIndex + 1 },
                            )}
                            onImageChange={(file) =>
                              updateWrongAnswerImage(item.id, wrongIndex, file)
                            }
                            onImageDelete={() =>
                              removeWrongAnswerImage(item.id, wrongIndex)
                            }
                            audioInputId={`wrong-answer-sound-${item.id}-${wrongIndex}`}
                            audioFile={wrongChoice.soundFile}
                            audioUrl={wrongChoice.sound}
                            onAudioChange={(file) =>
                              updateWrongAnswerSound(item.id, wrongIndex, file)
                            }
                            onAudioDelete={() =>
                              removeWrongAnswerSound(item.id, wrongIndex)
                            }
                            disableDelete={isSubmitting}
                            size="sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeWrongChoice(item.id, wrongIndex)}
                          className={`shrink-0 ${MEDIA_BTN_DELETE_SM}`}
                          title={tCompleteTheSentence("deleteWrongAnswer")}
                        >
                          <MdDeleteOutline size={16} />
                        </button>
                      </div>
                    ))}
                    {item.wrongChoices.length < 99 ? (
                      <div className="flex items-center gap-2">
                        <span className="w-6 shrink-0" aria-hidden="true" />
                        <button
                          type="button"
                          onClick={() => addWrongChoice(item.id)}
                          className="inline-flex w-fit items-center gap-1 rounded-md border border-[#ffb3b3] bg-[#fff5f5] px-3 py-1.5 text-sm font-semibold text-[#e02020] transition-colors hover:bg-[#ffecec] cursor-pointer"
                        >
                          {tCompleteTheSentence("addWrongAnswer")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  {item.hasHint ? (
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-bold text-[#555]">
                        {tCompleteTheSentence("hint")}
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex h-9.5 flex-1 items-center rounded-md border border-[#a8a8a8] bg-white px-3 focus-within:border-blue-400">
                          <input
                            value={item.hintText}
                            onChange={(event) =>
                              updateHintText(item.id, event.target.value)
                            }
                            className="h-full w-full min-w-0 bg-transparent text-sm outline-none"
                            placeholder={tCompleteTheSentence("typeHint")}
                          />
                          <div className="flex items-center gap-2">
                            <MediaEditGroup
                              imageInputId={hintImageId}
                              imageFile={item.imageHintFile}
                              imageUrl={item.imageHintUrl}
                              imageAlt={tCompleteTheSentence("hintImageAlt")}
                              onImageChange={(file) =>
                                setFileOnItem(item.id, {
                                  file,
                                  fileNameField: "imageHintFileName",
                                  fileField: "imageHintFile",
                                  urlField: "imageHintUrl",
                                  forceEnableHint: true,
                                })
                              }
                              audioInputId={hintAudioId}
                              audioFile={item.audioHintFile}
                              audioUrl={item.audioHintUrl}
                              onAudioChange={(file) =>
                                setFileOnItem(item.id, {
                                  file,
                                  fileNameField: "audioHintFileName",
                                  fileField: "audioHintFile",
                                  urlField: "audioHintUrl",
                                  forceEnableHint: true,
                                })
                              }
                              onImageDelete={() =>
                                requestDeleteMediaField(item.id, "image_hint")
                              }
                              onAudioDelete={() =>
                                requestDeleteMediaField(item.id, "sound_hint")
                              }
                              disableDelete={isSubmitting || isDeletingMedia}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                requestDeleteMediaField(item.id, "hint")
                              }
                              className={`${MEDIA_BTN_DELETE_MD} disabled:cursor-not-allowed disabled:opacity-50`}
                              title={tCompleteTheSentence("clearHint")}
                            >
                              <MdDeleteOutline size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        updateItem(item.id, (it) => ({ ...it, hasHint: true }))
                      }
                      className="cursor-pointer text-xs font-medium text-[#4ca2e2] hover:text-blue-600 hover:underline"
                    >
                      {tCompleteTheSentence("addHint")}
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => setPendingDeleteItemId(item.id)}
                  disabled={!canRemoveItem}
                  className={`${MEDIA_BTN_DELETE_MD} disabled:cursor-not-allowed disabled:opacity-50`}
                  title={
                    !canRemoveItem
                      ? tCompleteTheSentence("minItemsRequired", {
                          count: MIN_ITEMS,
                        })
                      : tCompleteTheSentence("delete")
                  }
                >
                  <MdDeleteOutline size={18} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={addItem}
          className="cursor-pointer rounded border border-[#c2c2c2] bg-[#e6e6e6] px-4 py-1.5 text-lg font-medium text-[#444] hover:bg-[#d6d6d6] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {tCompleteTheSentence("addItem")}
        </button>
      </div>

      <div className="mt-5 w-full p-3">
        <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">
          {tMain("expectedTimeLabel")}
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={expectedTime}
            onChange={(event) => {
              const nextValue = event.target.value;
              if (/^\d*$/.test(nextValue)) {
                setExpectedTime(nextValue);
              }
            }}
            required
            inputMode="numeric"
            className="h-10 w-full max-w-[360px] rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tMain("expectedTimePlaceholder")}
          />
          {expectedTimeDisplay ? (
            <span className="text-base font-semibold text-[#4a4a4a]">
              {expectedTimeDisplay}
            </span>
          ) : null}
        </div>
      </div>

      <div className="w-full p-3">
        <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-[#e0e0e0] bg-[#f9f9f9] p-4">
            <div className="flex items-start gap-3 pb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f0f0f0]">
                <TbCategory2 className="text-xl text-[#555]" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-[#333]">
                  {tCompleteTheSentence("competency")}
                </div>
                <div className="text-sm text-[#888]">
                  {tCompleteTheSentence("competencySubtitle")}
                </div>
              </div>
            </div>
            <hr className="mb-4 border-t border-[#e8e8e8]" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-stretch">
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <GiBrain className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tCompleteTheSentence("listeningSpeakingThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_listening_speaking}
                  onChange={(event) =>
                    updateGameSkill(
                      "skill_listening_speaking",
                      event.target.checked,
                    )
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <HiOutlineBookOpen className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tCompleteTheSentence("readingForThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_reading}
                  onChange={(event) =>
                    updateGameSkill("skill_reading", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <MdOutlineChat className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tCompleteTheSentence("understandingThaiLanguage")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_lang_nature}
                  onChange={(event) =>
                    updateGameSkill("skill_lang_nature", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <HiOutlinePencilAlt className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tCompleteTheSentence("writingForThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_writing}
                  onChange={(event) =>
                    updateGameSkill("skill_writing", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
            </div>
          </div>

          <div className="rounded-lg border border-[#e0e0e0] bg-[#f9f9f9] p-4">
            <div className="flex items-start gap-3 pb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f0f0f0]">
                <HiOutlineVolumeUp className="text-xl text-[#555]" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-[#333]">
                  {tCompleteTheSentence("sound")}
                </div>
                <div className="text-sm text-[#888]">
                  {tCompleteTheSentence("soundSubtitle")}
                </div>
              </div>
            </div>
            <hr className="mb-4 border-t border-[#e8e8e8]" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="overflow-hidden rounded-lg border border-[#e0e0e0]">
                <div className="flex items-center gap-2 bg-[#f0f4f7] px-3 py-2.5">
                  <HiOutlineMicrophone className="text-lg text-[#555]" />
                  <span className="text-sm font-semibold text-[#444]">
                    {tCompleteTheSentence("narration")}
                  </span>
                </div>
                <div className="space-y-3 bg-white p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_question}
                      onChange={(event) =>
                        updateGameAudio("audio_question", event.target.checked)
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tCompleteTheSentence("muteNarration")}
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_choice}
                      onChange={(event) =>
                        updateGameAudio("audio_choice", event.target.checked)
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tCompleteTheSentence("muteOptionNarration")}
                  </label>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-[#e0e0e0]">
                <div className="flex items-center gap-2 bg-[#f0f4f7] px-3 py-2.5">
                  <IoGameControllerOutline className="text-lg text-[#555]" />
                  <span className="text-sm font-semibold text-[#444]">
                    {tCompleteTheSentence("gameSound")}
                  </span>
                </div>
                <div className="space-y-3 bg-white p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_assistant_voice}
                      onChange={(event) =>
                        updateGameAudio(
                          "audio_assistant_voice",
                          event.target.checked,
                        )
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tCompleteTheSentence("muteAssistant")}
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_background}
                      onChange={(event) =>
                        updateGameAudio(
                          "audio_background",
                          event.target.checked,
                        )
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tCompleteTheSentence("muteBackground")}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
        {isUpdateMode && previewUuid ? (
          <button
            type="button"
            onClick={handleOpenPreview}
            disabled={hasUnsavedChanges || isSubmitting || !isExpectedTimeValid}
            className={GAME_PREVIEW_BUTTON_CLASS}
            title={
              hasUnsavedChanges
                ? PREVIEW_BUTTON_DISABLED_TITLE
                : tCompleteTheSentence("preview")
            }
          >
            {tCompleteTheSentence("preview")}
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleClickFinishButton}
          disabled={isSubmitting || !isSaveReady}
          className="cursor-pointer rounded bg-[#f4d735] px-7 py-2 text-lg font-semibold text-[#4a4a4a] shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:bg-[#e0c91a] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSubmitting
            ? tCompleteTheSentence("saving")
            : tCompleteTheSentence("save")}
        </button>
      </div>

      {isConfirmSaveOpen ? (
        <ConfirmPopUp
          title={tCompleteTheSentence("confirmSave")}
          message={tCompleteTheSentence("savePrompt")}
          cancelText={tCompleteTheSentence("cancel")}
          confirmText={tCompleteTheSentence("confirm")}
          onCancel={() => setIsConfirmSaveOpen(false)}
          onConfirm={handleConfirmSave}
          isConfirming={isSubmitting}
        />
      ) : null}

      {isStatusConfirmOpen ? (
        <ConfirmPopUp
          title={
            getStatusToggleDialogContent(isPublished, {
              confirmPublish: tCompleteTheSentence("confirmPublish"),
              publishPrompt: tCompleteTheSentence("publishPrompt"),
              published: tCompleteTheSentence("published"),
              publishedMessage: tCompleteTheSentence("publishedMessage"),
              confirmUnpublish: tCompleteTheSentence("confirmUnpublish"),
              unpublishPrompt: tCompleteTheSentence("unpublishPrompt"),
              unpublished: tCompleteTheSentence("unpublished"),
              unpublishedMessage: tCompleteTheSentence("unpublishedMessage"),
            }).confirmTitle
          }
          message={
            getStatusToggleDialogContent(isPublished, {
              confirmPublish: tCompleteTheSentence("confirmPublish"),
              publishPrompt: tCompleteTheSentence("publishPrompt"),
              published: tCompleteTheSentence("published"),
              publishedMessage: tCompleteTheSentence("publishedMessage"),
              confirmUnpublish: tCompleteTheSentence("confirmUnpublish"),
              unpublishPrompt: tCompleteTheSentence("unpublishPrompt"),
              unpublished: tCompleteTheSentence("unpublished"),
              unpublishedMessage: tCompleteTheSentence("unpublishedMessage"),
            }).confirmMessage
          }
          confirmText={tCompleteTheSentence("confirm")}
          cancelText={tCompleteTheSentence("cancel")}
          onConfirm={() => {
            void confirmStatusToggle();
          }}
          onCancel={closeStatusConfirm}
          isConfirming={isStatusConfirming}
        />
      ) : null}

      {pendingDeleteItemId !== null ? (
        <ConfirmPopUp
          title={tCompleteTheSentence("confirmDelete")}
          message={tCompleteTheSentence("deletePrompt")}
          cancelText={tCompleteTheSentence("cancel")}
          confirmText={tCompleteTheSentence("ok")}
          onCancel={() => setPendingDeleteItemId(null)}
          onConfirm={async () => {
            const idToDelete = pendingDeleteItemId;
            if (idToDelete === null) {
              setPendingDeleteItemId(null);
              return;
            }
            await removeItem(idToDelete);
            setPendingDeleteItemId(null);
          }}
        />
      ) : null}

      {pendingMediaDelete !== null ? (
        <ConfirmPopUp
          title={tCompleteTheSentence("confirmDelete")}
          message={tCompleteTheSentence("deleteFieldPrompt", {
            field: getDeleteFieldLabel(pendingMediaDelete.field),
          })}
          cancelText={tCompleteTheSentence("cancel")}
          confirmText={tCompleteTheSentence("ok")}
          onCancel={() => {
            if (isDeletingMedia) return;
            setPendingMediaDelete(null);
          }}
          onConfirm={() => {
            void confirmDeleteMediaField();
          }}
          isConfirming={isDeletingMedia}
        />
      ) : null}

      {statusPopUp ? (
        <StatusPopUp
          title={statusPopUp.title}
          message={statusPopUp.message}
          type={statusPopUp.type}
          onClose={handleCloseStatusPopUp}
        />
      ) : null}

      {previewIframeUrl ? (
        <GamePreviewModal
          src={previewIframeUrl}
          iframeTitle={tCompleteTheSentence("previewTitle")}
          onClose={() => setPreviewIframeUrl(null)}
          closeVariant="danger"
          shellVariant="editor"
        />
      ) : null}
    </section>
  );
}
